import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Overline, Reveal } from "@/components/Reveal";
import { COMMITMENTS } from "@/data/campaign";

const CommitmentDetail = () => {
  const { slug } = useParams();
  const index = COMMITMENTS.findIndex((c) => c.slug === slug);
  if (index === -1) return <Navigate to="/commitments" replace />;
  const c = COMMITMENTS[index];
  const prev = COMMITMENTS[(index - 1 + COMMITMENTS.length) % COMMITMENTS.length];
  const next = COMMITMENTS[(index + 1) % COMMITMENTS.length];

  return (
    <main data-testid="commitment-detail-page" className="pt-28 lg:pt-32">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Link
            to="/commitments"
            data-testid="detail-back-link"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60 transition-colors hover:text-terra-500"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" />
            All six commitments
          </Link>
          <div className="mt-8 flex flex-wrap items-start gap-6">
            <span className="font-serif text-7xl font-semibold leading-none text-pine-700/15 sm:text-8xl">{c.number}</span>
            <h1 className="max-w-3xl flex-1 font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              {c.title}
            </h1>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="img-frame overflow-hidden">
            <img src={c.img} alt={c.title} data-testid="detail-image" className="h-72 w-full object-cover sm:h-96" />
          </div>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <Overline>Why It Matters to Ward 11</Overline>
            <p className="mt-5 font-serif text-xl leading-relaxed text-pine-700 sm:text-2xl">{c.why}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <Overline>What I Will Do</Overline>
            <ul className="mt-5 space-y-5" data-testid="detail-actions-list">
              {c.actions.map((a) => (
                <li key={a} className="flex gap-4 border-b border-pine-700/10 pb-5">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center bg-pine-700 text-paper">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-base leading-relaxed text-ink/80">{a}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/get-involved?tab=priority"
              data-testid="detail-priority-cta"
              className="group mt-8 inline-flex items-center gap-2 bg-pine-700 px-7 py-4 text-sm font-semibold text-paper transition-colors hover:bg-terra-500"
            >
              Tell me what matters on your street
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mt-24 border-t border-pine-700/15 bg-parch">
        <div className="mx-auto grid max-w-7xl sm:grid-cols-2">
          <Link
            to={`/commitments/${prev.slug}`}
            data-testid="detail-prev-link"
            className="group border-b border-pine-700/15 p-6 transition-colors hover:bg-pine-900 hover:text-paper sm:border-b-0 sm:border-r sm:p-10"
          >
            <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/50 group-hover:text-terra-400">
              <ArrowLeft className="h-3.5 w-3.5" /> Previous · {prev.number}
            </span>
            <p className="mt-3 font-serif text-2xl font-medium tracking-tight">{prev.title}</p>
          </Link>
          <Link
            to={`/commitments/${next.slug}`}
            data-testid="detail-next-link"
            className="group p-6 text-right transition-colors hover:bg-pine-900 hover:text-paper sm:p-10"
          >
            <span className="flex items-center justify-end gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/50 group-hover:text-terra-400">
              Next · {next.number} <ArrowRight className="h-3.5 w-3.5" />
            </span>
            <p className="mt-3 font-serif text-2xl font-medium tracking-tight">{next.title}</p>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default CommitmentDetail;
